import { z } from 'zod';
/** Shared management DTOs. Tenant id is never an authority field on these bodies. */
export const membershipRoleSchema = z.enum(['TENANT_ADMIN', 'TENANT_VIEWER']);
export const normalizedEmailSchema = z.string().trim().max(320).toLowerCase().pipe(z.email());
export const loginRequestSchema = z
    .object({
    email: normalizedEmailSchema,
    password: z.string().min(1).max(128),
})
    .strict();
export const passwordChangeRequestSchema = z
    .object({
    currentPassword: z.string().min(1).max(128),
    newPassword: z.string().min(1).max(128),
})
    .strict();
export const forgotPasswordRequestSchema = z
    .object({
    email: normalizedEmailSchema,
})
    .strict();
export const resetPasswordRequestSchema = z
    .object({
    token: z.string().min(20).max(200),
    password: z.string().min(1).max(128),
})
    .strict();
export const acceptInviteRequestSchema = z
    .object({
    token: z.string().min(20).max(200),
    password: z.string().min(1).max(128).optional(),
})
    .strict();
export const membershipSummarySchema = z.object({
    tenantId: z.uuid(),
    tenantName: z.string(),
    role: membershipRoleSchema,
});
export const adminUiLocaleSchema = z.enum(['el', 'en']);
export const authUserSchema = z.object({
    id: z.uuid(),
    email: z.email(),
    platformRole: z.enum(['PLATFORM_ADMIN']).nullable(),
    mustChangePassword: z.boolean(),
    uiLocale: adminUiLocaleSchema.nullable(),
    memberships: z.array(membershipSummarySchema),
});
export const updateUiLocaleRequestSchema = z
    .object({
    locale: adminUiLocaleSchema,
})
    .strict();
export const sessionResponseSchema = z.object({
    accessToken: z.string().min(1),
    expiresAt: z.iso.datetime(),
    user: authUserSchema,
});
/** Payment term used when a subscription invoice is issued. Matches the previous fixed due offset. */
export const DEFAULT_CREDIT_DAYS = 14;
const CREDIT_DAYS_MESSAGE = 'Οι ημέρες πίστωσης είναι ακέραιος από 0 έως 365.';
/** Greek AFM checksum: weighted sum of the first 8 digits, check digit `(sum % 11) % 10`. */
export function isGreekTaxId(value) {
    if (!/^\d{9}$/.test(value) || value === '000000000')
        return false;
    let sum = 0;
    for (let index = 0; index < 8; index += 1) {
        sum += Number(value[index]) * 2 ** (8 - index);
    }
    return (sum % 11) % 10 === Number(value[8]);
}
function blankToNull(value) {
    if (typeof value !== 'string')
        return value;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
}
function optionalText(max, message) {
    return z.preprocess(blankToNull, z.union([z.string().max(max, { error: message }), z.null()]).optional());
}
const creditDaysSchema = z
    .number({ error: CREDIT_DAYS_MESSAGE })
    .int({ error: CREDIT_DAYS_MESSAGE })
    .min(0, { error: CREDIT_DAYS_MESSAGE })
    .max(365, { error: CREDIT_DAYS_MESSAGE })
    .optional();
const taxIdSchema = z.preprocess(blankToNull, z
    .union([
    z
        .string()
        .regex(/^\d{9}$/, { error: 'Το ΑΦΜ έχει 9 ψηφία.' })
        .refine(isGreekTaxId, { error: 'Το ΑΦΜ δεν είναι έγκυρο.' }),
    z.null(),
])
    .optional());
const postalCodeSchema = z.preprocess(blankToNull, z.union([z.string().regex(/^\d{5}$/, { error: 'Ο ΤΚ έχει 5 ψηφία.' }), z.null()]).optional());
const phoneSchema = z.preprocess(blankToNull, z
    .union([
    z
        .string()
        .max(40, { error: 'Το τηλέφωνο δεν είναι έγκυρο.' })
        .regex(/^[0-9+().\-\s]+$/, { error: 'Το τηλέφωνο δεν είναι έγκυρο.' })
        .refine((value) => {
        const digits = value.replace(/\D/g, '').length;
        return digits >= 10 && digits <= 15;
    }, { error: 'Το τηλέφωνο δεν είναι έγκυρο.' }),
    z.null(),
])
    .optional());
const billingEmailSchema = z.preprocess((value) => {
    if (typeof value !== 'string')
        return value;
    const trimmed = value.trim().toLowerCase();
    return trimmed.length === 0 ? null : trimmed;
}, z.union([z.email({ error: 'Το email δεν είναι έγκυρο.' }), z.null()]).optional());
/** Optional invoicing profile. Empty strings clear the stored value. Omitted keys are left unchanged. */
export const tenantBillingFieldsSchema = z.object({
    creditDays: creditDaysSchema,
    taxId: taxIdSchema,
    taxOffice: optionalText(120, 'Η Δ.Ο.Υ. είναι πολύ μεγάλη.'),
    profession: optionalText(200, 'Το επάγγελμα είναι πολύ μεγάλο.'),
    street: optionalText(200, 'Η οδός είναι πολύ μεγάλη.'),
    streetNumber: optionalText(20, 'Ο αριθμός είναι πολύ μεγάλος.'),
    city: optionalText(100, 'Η πόλη είναι πολύ μεγάλη.'),
    postalCode: postalCodeSchema,
    phone: phoneSchema,
    email: billingEmailSchema,
    notes: optionalText(2000, 'Οι σημειώσεις είναι πολύ μεγάλες.'),
});
export const tenantSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    status: z.enum(['ACTIVE', 'SUSPENDED']),
    timezone: z.string(),
    creditDays: z.number().int(),
    taxId: z.string().nullable(),
    taxOffice: z.string().nullable(),
    profession: z.string().nullable(),
    street: z.string().nullable(),
    streetNumber: z.string().nullable(),
    city: z.string().nullable(),
    postalCode: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const createTenantRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    timezone: z.string().trim().min(1).max(64).optional(),
})
    .extend(tenantBillingFieldsSchema.shape)
    .strict();
export const updateTenantRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    status: z.enum(['ACTIVE', 'SUSPENDED']).optional(),
    timezone: z.string().trim().min(1).max(64).optional(),
})
    .extend(tenantBillingFieldsSchema.shape)
    .strict();
export const siteSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    canonicalOrigin: z.string(),
    status: z.enum(['ACTIVE', 'DISABLED']),
    timezone: z.string(),
    createdAt: z.iso.datetime(),
});
export const createSiteRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    canonicalOrigin: z.string().trim().min(1).max(255),
    timezone: z.string().trim().min(1).max(64).optional(),
})
    .strict();
export const updateSiteRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    status: z.enum(['ACTIVE', 'DISABLED']).optional(),
    timezone: z.string().trim().min(1).max(64).optional(),
})
    .strict();
export const inviteMemberRequestSchema = z
    .object({
    email: normalizedEmailSchema,
    role: membershipRoleSchema,
})
    .strict();
export const updateMemberRequestSchema = z
    .object({
    role: membershipRoleSchema,
})
    .strict();
export const memberSchema = z.object({
    userId: z.uuid(),
    email: z.email(),
    role: membershipRoleSchema,
    status: z.enum(['INVITED', 'ACTIVE', 'DISABLED']),
});
export const assetMetadataSchema = z.object({
    id: z.uuid(),
    siteId: z.uuid().nullable(),
    type: z.enum(['PDF', 'LOGO']),
    mime: z.string(),
    bytes: z.number().int(),
    status: z.enum(['STORED', 'DELETED']),
});
//# sourceMappingURL=management.js.map